/**
 * Default mutation and query handlers that connect collection configurations to
 * REST endpoints. These handlers keep transactional writes consistent while the
 * collection builder composes runtime behaviour.
 */
import type { QueryFunctionContext } from '@tanstack/query-core'
import type {
  DeleteMutationFn,
  DeleteMutationFnParams,
  Fn,
  InferSchemaOutput,
  InsertMutationFn,
  InsertMutationFnParams,
  UpdateMutationFn,
  UpdateMutationFnParams,
  UtilsRecord,
} from '@tanstack/vue-db'
import { toValue } from 'vue'
import { useRhythmicLogger } from '#imports'
import {
  buildSearchParams,
  createMutationResult,
  normaliseChanges,
  resolveApiEndpoint,
} from './CollectionBuilder.helpers'
import type { CollectionBuildRequestConfig } from './CollectionBuilder.types'

const logger = useRhythmicLogger('collection-builder-mutators')

const JSON_HEADERS = {
  'Content-Type': 'application/json',
  accept: 'application/json',
} as const

const gatewayErrorLogIntervalMs = 60_000
const gatewayErrorLogState = {
  lastLoggedAt: 0,
  suppressedCount: 0,
}

const shouldThrottleGatewayErrorLogs = () => typeof process !== 'undefined' && process.env.NODE_ENV === 'production'

function logQueryFailure(table: string, status: number) {
  if (status === 502 && shouldThrottleGatewayErrorLogs()) {
    const now = Date.now()
    const elapsed = now - gatewayErrorLogState.lastLoggedAt

    if (elapsed >= gatewayErrorLogIntervalMs) {
      const suppressed = gatewayErrorLogState.suppressedCount
      gatewayErrorLogState.lastLoggedAt = now
      gatewayErrorLogState.suppressedCount = 0
      logger.warn('Query request returned 502 (throttled)', {
        table,
        suppressed,
      })
    } else {
      gatewayErrorLogState.suppressedCount += 1
    }
    return
  }

  logger.error('Query request failed', { table, status })
}

function consolidateTxids(txids: Array<number | undefined>): number | number[] | undefined {
  const filtered = txids.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  if (filtered.length === 0) return undefined
  return filtered.length === 1 ? filtered[0] : filtered
}

/**
 * Sends insert mutations to the configured API endpoint, falling back to
 * per-record requests when the target URL includes placeholder path segments.
 */
async function defaultOnInsert<TItem extends Record<string, unknown>, TKey extends string | number>(
  params: InsertMutationFnParams<TItem, TKey, UtilsRecord>,
  config: CollectionBuildRequestConfig,
) {
  const mutations = params.transaction.mutations

  if (!config.apiEndpoint) {
    logger.error('Insert skipped because apiEndpoint is missing', {
      table: config.table,
    })
    throw new Error('Insert skipped because apiEndpoint is missing')
  }

  if (mutations.length === 0) {
    return { txid: undefined }
  }

  const txids = await Promise.all(
    mutations.map(async (mutation) => {
      try {
        const endpoint = resolveApiEndpoint(config, {
          record: mutation.modified as Record<string, unknown> | undefined,
        })
        const body = normaliseChanges(mutation.modified as Record<string, unknown>)

        const response = (await $fetch(endpoint, {
          method: 'POST',
          headers: JSON_HEADERS,
          credentials: 'include',
          body: JSON.stringify(body),
        })) as any
        return Number.parseInt(response.txid)
      } catch (error) {
        logger.warn('Insert mutation request failed', {
          table: config.table,
          error,
        })
      }
    }),
  )
  const validTxids = txids.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))

  if (validTxids.length === 0) return { txid: [] }

  return { txid: validTxids.length === 1 ? validTxids[0] : validTxids }
}

// }))
/**
 * Applies granular change sets to existing records by issuing `PUT` requests
 * for each mutation in the transaction batch.
 */
async function defaultOnUpdate(params: UpdateMutationFnParams, config: CollectionBuildRequestConfig) {
  if (!config.apiEndpoint) {
    logger.warn('Update skipped because apiEndpoint is missing', {
      table: config.table,
    })
    return createMutationResult()
  }
  logger.debug('Processing update mutations', {
    table: config.table,
    mutations: params.transaction.mutations,
  })

  try {
    const txids = await Promise.all(
      params.transaction.mutations.map(async (mutation) => {
        const candidateId = mutation.modified?.[config.key] as string | number | undefined
        if (candidateId === undefined || candidateId === null) return undefined

        const changes = mutation.changes
        if (Object.keys(changes).length === 0) {
          return undefined
        }

        const recordContext = (mutation.modified ?? mutation.original ?? config.where) as
          | Record<string, unknown>
          | undefined
        const resourceUrl = resolveApiEndpoint(config, {
          record: recordContext,
          resourceId: candidateId,
        })
        const response = (await $fetch(resourceUrl, {
          method: 'PUT',
          headers: JSON_HEADERS,
          credentials: 'include',
          body: JSON.stringify(changes),
        })) as any

        if (response?.error) {
          logger.error('Update mutation request failed', {
            table: config.table,
            key: candidateId,
            error: response.error,
          })
          throw response.error
        }

        const txidCandidate = Number.parseInt(response.txid)
        const txid = Number.isNaN(txidCandidate) ? undefined : txidCandidate

        return txid
      }),
    )

    return { txid: consolidateTxids(txids) }
  } catch (error) {
    logger.error('Update mutation threw', { table: config.table, error })
    throw error
  }
}

/**
 * Issues `DELETE` requests for each record scheduled for removal, surfacing the
 * most recent transaction id returned by the backend.
 */
async function defaultOnDelete(params: DeleteMutationFnParams, config: CollectionBuildRequestConfig) {
  if (!config.apiEndpoint) {
    logger.warn('Delete skipped because apiEndpoint is missing', {
      table: config.table,
    })
    return createMutationResult()
  }

  try {
    const txids = await Promise.all(
      params.transaction.mutations.map(async (mutation) => {
        const candidateId = mutation.key as string | number | undefined
        if (candidateId === undefined || candidateId === null) return undefined

        const recordContext = (mutation.modified ?? config.where) as Record<string, unknown> | undefined
        const resourceUrl = resolveApiEndpoint(config, {
          record: recordContext,
          resourceId: candidateId,
        })
        const response = (await $fetch(resourceUrl, {
          method: 'DELETE',
          headers: { accept: 'application/json' },
          credentials: 'include',
        })) as any
        logger.debug('Delete mutation request sent', {
          table: config.table,
          key: candidateId,
          response,
          resourceUrl,
          data: response,
        })

        const responseError = toValue(response.error)
        if (responseError) {
          logger.error('Delete mutation request failed', {
            table: config.table,
            key: candidateId,
            error: responseError,
          })
          throw responseError
        }

        const txid = Number.parseInt(toValue(response).txid)
        logger.debug('Delete mutation completed', {
          table: config.table,
          key: candidateId,
          txid,
        })
        return txid
      }),
    )

    return { txid: consolidateTxids(txids) }
  } catch (error) {
    logger.error('Delete mutation threw', { table: config.table, error })
    throw error
  }
}

/**
 * Default query implementation that resolves the configured endpoint and
 * returns an array payload, logging anomalies when the response differs.
 */
async function defaultQueryFn(
  context: QueryFunctionContext,
  config: CollectionBuildRequestConfig,
): Promise<Array<unknown>> {
  const baseEndpoint = resolveApiEndpoint(config, { record: config.where })
  const query = buildSearchParams(config)

  const separator = baseEndpoint.includes('?') ? '&' : '?'
  const target = query ? `${baseEndpoint}${separator}${query}` : baseEndpoint

  const headers: Record<string, string> = { accept: 'application/json' }
  if (import.meta.client) {
    try {
      const clerk = (globalThis as Record<string, any> | undefined)?.Clerk
      const sessionGetter = clerk?.session?.getToken as
        | ((options?: { template?: string }) => Promise<string | null>)
        | undefined
      if (typeof sessionGetter === 'function') {
        const token = await sessionGetter()
        if (token) headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      logger.error('Unable to attach Clerk token to query request', {
        error,
        table: config.table,
      })
    }
  }

  const response = await fetch(target, {
    method: 'GET',
    headers,
    credentials: 'include',
    signal: context.signal,
  })

  if (!response.ok) {
    const error = new Error(`Failed to fetch data for ${config.table} (status ${response.status})`)
    logQueryFailure(config.table, response.status)
    throw error
  }

  try {
    const payload = await response.json()
    if (Array.isArray(payload)) return payload as Array<unknown>

    logger.warn('Query response was not an array; returning empty list', {
      table: config.table,
      payload,
    })
    return []
  } catch (error) {
    logger.error('Failed to parse query response as JSON', {
      table: config.table,
      error,
    })
    throw error
  }
}

/**
 * Builds a set of default CRUD handlers wired to the provided collection
 * configuration, ready to be consumed by the reactive-db collection builder.
 */
export function createDefaultHandlers(config: CollectionBuildRequestConfig) {
  type schema = typeof config.schema
  return {
    onInsert: (async (params: InsertMutationFnParams) => defaultOnInsert(params, config)) as InsertMutationFn<
      InferSchemaOutput<NonNullable<schema>>,
      string | number,
      Record<string, Fn>,
      { txid: number | number[] }
    >,
    onUpdate: (async (params: UpdateMutationFnParams) => defaultOnUpdate(params, config)) as UpdateMutationFn<
      InferSchemaOutput<NonNullable<schema>>,
      string | number,
      Record<string, Fn>,
      { txid: number | number[] }
    >,
    onDelete: (async (params: DeleteMutationFnParams) => defaultOnDelete(params, config)) as DeleteMutationFn<
      InferSchemaOutput<NonNullable<schema>>,
      string | number,
      Record<string, Fn>,
      { txid: number | number[] }
    >,
    queryFn: (async (context: QueryFunctionContext) => defaultQueryFn(context, config)) as unknown,
  }
}
